<script setup="">
import { computed } from "vue";

import useCampaignDrawer from "@/composables/campaignDrawer";
import useDrawer from "@/composables/drawer";
import { useScrollStore } from "@/stores/scroll";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  team: {
    type: Object,
    required: false
  },
  page: {
    type: Object,
    required: false
  },
  isLoggedIn: {
    type: Boolean,
    required: true
  },
  canDesign: {
    type: Boolean,
    required: true
  }
});

const scroll = useScrollStore();
const campaignSettingDrawer = useDrawer("campaign-editor");
const pageCommentDrawer = useCampaignDrawer("page:comment");

const enabledKakaoChannel = computed(
  () => props.campaign.settings?.quick_links?.kakao_channel?.enabled && props.campaign.settings?.quick_links?.kakao_channel?.id
);
const enabledKakaoOpenChat = computed(
  () => props.campaign.settings?.quick_links?.kakao_open_chat?.enabled && props.campaign.settings?.quick_links?.kakao_open_chat?.id
);
</script>

<template>
  <div class="fixed top-1/2 z-10 transition" :class="[scroll.isDown ? 'right-0 translate-x-full xl:right-4 xl:translate-x-0' : 'right-2 xl:right-4']">
    <ul class="space-y-4">
      <li v-if="enabledKakaoOpenChat">
        <a
          :href="'https://pf.kakao.com/' + props.campaign.settings.quick_links.kakao_channel.id + '/chat'"
          v-tippy="{ content: '카카오톡 오픈채팅', placement: 'left', touch: false }"
          target="_blank"
          class="flex-center z-1 flex h-12 w-12 rounded-full bg-[#fff]/70 text-black shadow backdrop-blur-sm transition hover:scale-95 lg:h-14 lg:w-14 lg:bg-[#fff]/90"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="24" fill="none" viewBox="0 0 22 24" class="h-6 w-6">
            <path
              fill="#F1D825"
              fill-rule="evenodd"
              d="M10.99 2.313c-1.712.424-4.05 1.785-4.05 2.359 0 .088.652.214 1.448.28 2.715.223 5.336 2.05 6.492 4.525.486 1.042.745 2.438.62 4.023-.16 2.028-.32 2.665-1.918 4.842-.47.64-.853 1.262-.853 1.382 0 .124.373.17.864.108.78-.1 1.019.003 2.434 1.042 2.436 1.788 3.088 1.504 3.09-1.345.002-1.67.004-1.676.849-2.613.465-.516 1.116-1.53 1.445-2.254.556-1.222.59-3.683.59-3.683s-.055-2.463-.633-3.72c-1.156-2.514-3.335-4.363-5.843-4.96-1.699-.403-2.864-.4-4.534.014Z"
              clip-rule="evenodd"
            />
            <path
              fill="#000"
              fill-rule="evenodd"
              d="M6.006 5.814c-2.448.467-4.465 2.051-5.373 4.22-1.096 2.616-.756 5.395.925 7.551.736.944.791 1.112.791 2.378 0 1.151.069 1.416.444 1.712.626.495.982.442 1.872-.282 1.072-.872 1.425-1.005 2.575-.965 2.133.074 4.731-1.242 6.08-3.079.807-1.1 1.493-3.603 1.338-4.882-.527-4.348-4.545-7.437-8.652-6.653Z"
              clip-rule="evenodd"
            />
          </svg>
        </a>
      </li>
      <li v-if="enabledKakaoChannel">
        <a
          :href="'https://pf.kakao.com/' + props.campaign.settings.quick_links.kakao_channel.id + '/chat'"
          v-tippy="{ content: '카카오 채널', placement: 'left', touch: false }"
          target="_blank"
          class="flex-center z-1 flex h-12 w-12 rounded-full bg-[#fff]/70 text-black shadow backdrop-blur-sm transition hover:scale-95 lg:h-14 lg:w-14 lg:bg-[#fff]/90"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="24" fill="none" viewBox="0 0 22 24" class="h-6 w-6">
            <g clip-path="url(#a)">
              <path
                fill="#020202"
                d="M10.992.035C4.922.035 0 4.787 0 10.649c0 3.463 1.717 6.537 4.373 8.475v4.658c0 .066.035.126.092.156a.165.165 0 0 0 .175-.013l3.934-2.92a11.42 11.42 0 0 0 2.417.258c6.07 0 10.99-4.752 10.99-10.614 0-5.862-4.92-10.614-10.99-10.614Z"
              />
              <path
                fill="#F1D825"
                d="M17.853 9.42c-.373-.42-.925-.633-1.64-.633-.396 0-.758.09-1.075.27a2.598 2.598 0 0 0-.616.476V6.097h-1.555V15.2h1.544v-3.4c0-.283.037-.538.11-.76.073-.22.173-.409.297-.56.123-.15.268-.266.432-.346.162-.08.338-.12.521-.12.319 0 .567.09.739.268.172.177.259.507.259.98V15.2h1.544v-3.874c0-.846-.188-1.488-.56-1.906ZM7.711 7.989c1.084 0 2.009.729 2.378 1.756h1.659c-.42-1.987-2.067-3.472-4.037-3.472-2.286 0-4.14 1.999-4.14 4.464 0 2.466 1.854 4.464 4.14 4.464 1.97 0 3.618-1.484 4.037-3.471h-1.66c-.368 1.027-1.293 1.756-2.377 1.756-1.408 0-2.549-1.23-2.549-2.749 0-1.518 1.141-2.748 2.55-2.748Z"
              />
            </g>
            <defs>
              <clipPath id="a">
                <path fill="#fff" d="M0 0h22v24H0z" />
              </clipPath>
            </defs>
          </svg>
        </a>
      </li>
      <li v-if="page && page?.is_commentable">
        <button
          v-tippy="{ content: '댓글', placement: 'left', touch: false }"
          class="flex-center z-1 flex h-12 w-12 rounded-full bg-[#fff]/70 text-black shadow backdrop-blur-sm transition hover:scale-95 lg:h-14 lg:w-14 lg:bg-[#fff]/90"
          @click="pageCommentDrawer.toggle({ overlay: false })"
        >
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" class="mx-auto h-6 w-6">
              <path
                fill="#000"
                stroke="#000"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10Z"
              />
              <path stroke="#fff" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01" />
            </svg>
          </span>
        </button>
      </li>
      <li v-if="canDesign">
        <button
          v-tippy="{ content: '디자인', placement: 'left', touch: false }"
          class="flex-center z-1 flex h-12 w-12 rounded-full bg-primary text-[#fff] shadow backdrop-blur-sm transition hover:scale-95 lg:h-14 lg:w-14"
          @click="campaignSettingDrawer.toggle({ overlay: false })"
        >
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path
                fill="#fff"
                stroke="#000"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M18.376 2.622a2.122 2.122 0 1 1 3.002 3.002L17.36 9.643a.5.5 0 0 0 0 .707l.944.944a2.41 2.41 0 0 1 0 3.408l-.944.944a.5.5 0 0 1-.707 0L8.354 7.348a.5.5 0 0 1 0-.707l.944-.944a2.41 2.41 0 0 1 3.408 0l.944.944a.5.5 0 0 0 .707 0l4.02-4.019Z"
              />
              <path
                fill="#fff"
                d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15"
              />
              <path
                stroke="#000"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1"
                d="M9 8c-1.804 2.71-3.97 3.46-6.583 3.948a.507.507 0 0 0-.302.819l7.32 8.883a1 1 0 0 0 1.185.204C12.735 20.405 16 16.792 16 15m-1.378 2.897-10.68-2.913"
                class="h-6 w-6"
              />
            </svg>
          </span>
        </button>
      </li>
    </ul>
  </div>
</template>
